import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import SearchForm from '@justpark/ui/src/components/SearchForm';
import Button from '@justpark/ui/src/components/Button';
import { useLazyQuery } from '@apollo/client';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import localDateTimeIso8601 from '@justpark/helpers/src/localDateTimeIso8601/localDateTimeIso8601';
import { defaultSearchDates } from '../../helpers/date';
import { gtagTrack } from '../../helpers/Tracking/Analytics';
import GET_SEARCH from '../../queries/search/getSearch.graphql';

import styles from './searchFormModule.module.scss';

dayjs.extend(dayjsDuration);

type Props = {
  searchInputRef: any,
  defaultLocation?: {
    lat: number,
    lng: number
  }
};

const cypress = 'search-form';
const SearchFormModule = ({ searchInputRef, defaultLocation }: Props) => {
  const [searchData, setSearchData] = useState({
    query: '',
    bookingType: 'hourly',
    arriving: null,
    leaving: null,
    monthlyStarting: null,
    monthlyDuration: 'WEEKDAYS_ONLY',
    searchData: {}
  });

  const navigate = useNavigate();

  const [showValidation, setShowValidation] = useState(false);

  const isValid = useMemo(() => {
    const {
      searchData: { lat, lng }
    } = searchData;

    return defaultLocation !== undefined || (lat && lng);
  }, [searchData, defaultLocation]);

  const isFormComplete = useMemo(() => {
    const {
      bookingType,
      arriving,
      leaving,
      monthlyStarting,
      monthlyDuration,
      searchData: { lat, lng }
    } = searchData;

    if (bookingType === 'hourly') {
      return arriving && leaving && lat && lng;
    }

    return monthlyStarting && monthlyDuration && lat && lng;
  }, [searchData]);

  const coords = useMemo(() => {
    const {
      searchData: { lat, lng }
    } = searchData;

    if (lat && lng) {
      return {
        lat,
        lng
      };
    }

    return null;
  }, [searchData]);

  const filterQueryString = useMemo(() => {
    const { arriving } = searchData;
    const arrivingDate = dayjs(arriving);
    const leadTime = dayjs.duration(arrivingDate.diff(dayjs())).asMinutes();
    return leadTime > 30 ? 'reservable' : '';
  }, [searchData]);

  const onSubmit = () => {
    if (!isValid) {
      setShowValidation(true);
      return;
    }

    const isMonthlyBooking = searchData.bookingType !== 'hourly';

    gtagTrack('core:search-block:clicked-search', { isMonthlyBooking });

    const reservableFilter = filterQueryString
      ? `&filters=${filterQueryString}`
      : '';

    const defaultDates = defaultSearchDates();

    const { arriving, leaving, monthlyStarting, monthlyDuration } = searchData;

    const arrivingValue =
      arriving || defaultDates.arriving.format(localDateTimeIso8601);
    const leavingValue =
      leaving || defaultDates.leaving.format(localDateTimeIso8601);
    const monthlyStartingValue =
      monthlyStarting ||
      defaultDates.arriving.startOf('day').format(localDateTimeIso8601);
    const latValue = searchData.searchData.lat || defaultLocation?.lat;
    const lngValue = searchData.searchData.lng || defaultLocation?.lng;
    const queryValue = searchData.query;

    const dateQuery =
      searchData.bookingType === 'hourly'
        ? `&arriving=${arrivingValue}&leaving=${leavingValue}`
        : `&arriving=${monthlyStartingValue}&monthly_duration=0&monthly_type=${monthlyDuration}`;

    navigate({
      pathname: '/search',
      search: `?${dateQuery}${
        queryValue ? `&q=${queryValue}` : ''
      }&coords=${latValue},${lngValue}${
        searchData.searchData.googlePlaceId
          ? `&place_id=${searchData.searchData.googlePlaceId}`
          : ''
      }${reservableFilter}`
    });
  };

  const [getQuery] = useLazyQuery(GET_SEARCH);

  useEffect(() => {
    if (isFormComplete) {
      const {
        arriving,
        leaving,
        bookingType,
        monthlyStarting,
        monthlyDuration,
        query
      } = searchData;

      const dateDiff = calculateDateDiff(arriving, leaving);

      if (dateDiff >= 90) {
        return;
      }

      const { lat, lng, googlePlaceId } = searchData.searchData;

      getQuery({
        variables: {
          lat,
          lng,
          startDate: bookingType === 'monthly' ? monthlyStarting : arriving,
          endDate: leaving,
          monthlyApplicableDays:
            bookingType === 'monthly' ? monthlyDuration : undefined,
          googlePlaceId,
          userQuery: query,
          filters: filterQueryString
        }
      });
    }
  }, [searchData, filterQueryString, isFormComplete, getQuery]);

  const driveupListingId = searchData.searchData?.listingId;

  useEffect(() => {
    if (driveupListingId) {
      navigate({
        pathname: `/checkout/${driveupListingId}`
      });
    }
  }, [driveupListingId, navigate]);

  useEffect(() => {
    setShowValidation(false);
  }, [searchData.bookingType]);

  const setDefaultHourlyDates = useCallback(() => {
    setSearchData((existingData) => ({
      ...existingData,
      arriving: defaultSearchDates().arriving.format(localDateTimeIso8601),
      leaving: defaultSearchDates().leaving.format(localDateTimeIso8601)
    }));
  }, []);

  const setDefaultMonthlyDates = useCallback(() => {
    setSearchData((existingData) => ({
      ...existingData,
      monthlyStarting: defaultSearchDates()
        .arriving.startOf('day')
        .format(localDateTimeIso8601)
    }));
  }, []);

  return (
    <div className={styles.base}>
      <SearchForm
        onChange={setSearchData}
        query={searchData.query}
        coords={coords}
        arriving={searchData.arriving}
        leaving={searchData.leaving}
        monthlyStarting={searchData.monthlyStarting}
        monthlyDuration={searchData.monthlyDuration}
        bookingType={searchData.bookingType}
        searchInputRef={searchInputRef}
        showValidation={showValidation}
        setDefaultHourlyDates={setDefaultHourlyDates}
        setDefaultMonthlyDates={setDefaultMonthlyDates}
      />
      <Button
        className={styles.submitButton}
        onClick={onSubmit}
        cypress={`${cypress}-button`}
      >
        Show Parking Spaces
      </Button>
    </div>
  );
};

export default SearchFormModule;
